import { supabase } from './supabase';

export type KimiChatRole = 'user' | 'assistant';

export type KimiChatAttachmentKind = 'image' | 'file';

export type KimiChatAttachment = {
  kind: KimiChatAttachmentKind;
  name: string;
  mimeType: string;
  sizeBytes: number;
  url: string;
  storagePath?: string;
};

export type KimiChatMessage = {
  role: KimiChatRole;
  content: string;
  attachments?: KimiChatAttachment[];
  classroomReadResults?: SermonAgentActionResult[];
};

export type KimiChatContextMetadata = {
  contextVersion: string;
  sectionsUsed: string[];
  roleScope: 'member' | 'elevated';
};

export type SermonUploadTarget = 'confession_image' | 'points_image' | 'pdf_notes';

export type SermonAgentUploadRequest = {
  actionId: string;
  sermonId: string;
  target: SermonUploadTarget;
  bucket: string;
  path: string;
  token: string;
  signedUploadUrl: string | null;
  publicUrl: string;
  expiresAt: string;
  maxBytes: number;
  acceptedMimePrefixes: string[];
  suggestedFileName: string;
};

export type SermonAgentActionResult = {
  actionId: string;
  type: string;
  status: 'success' | 'error';
  summary: string;
  uploadRequest?: SermonAgentUploadRequest;
  entityType?:
    | 'users'
    | 'events'
    | 'event_requests'
    | 'event_ticket_types'
    | 'event_order_form_fields'
    | 'event_insights'
    | 'classes'
    | 'lessons'
    | 'assignments'
    | 'quizzes'
    | 'class_enrollments'
    | 'submissions'
    | 'class_discussion_posts'
    | 'class_discussion_settings'
    | 'announcements'
    | 'class_syllabi'
    | 'class_textbooks'
    | 'trivia_quizzes'
    | 'devotional_series'
    | 'devotionals';
  entityId?: string;
  details?: Record<string, unknown>;
};

export type PendingActionBundle = {
  token: string;
  expiresAt: string;
  draftChanges: string[];
  actionCount: number;
};

type KimiChatProxyResponse = {
  assistantMessage?: string;
  usage?: {
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
  } | null;
  context?: KimiChatContextMetadata | null;
  actionResults?: SermonAgentActionResult[] | null;
  pendingActionBundle?: PendingActionBundle | null;
  error?: string;
};

const stringifyUnknown = (value: unknown): string => {
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (!value || typeof value !== 'object') return '';
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
};

type InvokeErrorDetails = {
  message: string;
  httpStatus: number | null;
  statusText: string | null;
  responseBody: unknown;
  requestId: string | null;
  upstreamStatus: number | null;
  providerError: string | null;
  functionName: string;
  edgeFunction: string;
  actionName: string;
  recoverable: boolean;
};

const extractRequestId = (payload: unknown): string | null => {
  if (!payload || typeof payload !== 'object') return null;
  const body = payload as Record<string, unknown>;
  const candidate = body.requestId ?? body.request_id ?? body.errorId ?? body.error_id;
  return typeof candidate === 'string' && candidate.trim() ? candidate.trim() : null;
};

const extractInvokeErrorDetails = async (
  error: unknown,
  metadata: { functionName: string; actionName: string }
): Promise<InvokeErrorDetails> => {
  if (!error || typeof error !== 'object') {
    return {
      message: 'Unable to reach chat service.',
      httpStatus: null,
      statusText: null,
      responseBody: null,
      requestId: null,
      upstreamStatus: null,
      providerError: null,
      functionName: metadata.functionName,
      edgeFunction: metadata.functionName,
      actionName: metadata.actionName,
      recoverable: true,
    };
  }

  const err = error as { message?: string; context?: unknown };
  const responseLike =
    err.context && typeof err.context === 'object'
      ? (err.context as {
          status?: number;
          statusText?: string;
          headers?: { get?: (key: string) => string | null };
          clone?: () => { json?: () => Promise<unknown>; text?: () => Promise<string> };
          json?: () => Promise<unknown>;
          text?: () => Promise<string>;
        })
      : null;

  let payload: unknown = null;
  if (responseLike) {
    const source = typeof responseLike.clone === 'function' ? responseLike.clone() : responseLike;
    if (typeof source.json === 'function') {
      try {
        payload = await source.json();
      } catch {
        payload = null;
      }
    }
    if (!payload && typeof source.text === 'function') {
      try {
        const text = (await source.text()).trim();
        if (text) {
          try {
            payload = JSON.parse(text);
          } catch {
            payload = { error: text };
          }
        }
      } catch {
        payload = null;
      }
    }
  }

  const httpStatus = typeof responseLike?.status === 'number' ? responseLike.status : null;
  const statusText = typeof responseLike?.statusText === 'string' ? responseLike.statusText : null;

  let message = err.message?.trim() || 'Unable to reach chat service.';
  let upstreamStatus: number | null = null;
  let providerError: string | null = null;
  let requestId = extractRequestId(payload);

  if (payload && typeof payload === 'object') {
    const body = payload as Record<string, unknown>;
    upstreamStatus = typeof body.upstreamStatus === 'number' ? body.upstreamStatus : null;
    providerError = stringifyUnknown(body.providerError) || null;
    requestId = requestId ?? extractRequestId(body.details);

    const pieces = [
      stringifyUnknown(body.error),
      body.upstreamStatus != null ? `upstream ${String(body.upstreamStatus)}` : '',
      stringifyUnknown(body.providerError),
      stringifyUnknown(body.details),
    ].filter(Boolean);
    if (pieces.length) {
      message = pieces.join(' | ');
    }
  }

  if (!requestId && typeof responseLike?.headers?.get === 'function') {
    requestId = responseLike.headers.get('x-request-id') || responseLike.headers.get('x-correlation-id') || null;
  }

  return {
    message,
    httpStatus,
    statusText,
    responseBody: payload,
    requestId,
    upstreamStatus,
    providerError,
    functionName: metadata.functionName,
    edgeFunction: metadata.functionName,
    actionName: metadata.actionName,
    recoverable: httpStatus ? httpStatus < 500 : true,
  };
};

const MAX_LOCAL_MESSAGES = 20;
const MAX_ATTACHMENTS_PER_MESSAGE = 3;
const MAX_CHAT_ATTACHMENT_BYTES = 25 * 1024 * 1024;
const KIMI_CHAT_ATTACHMENT_BUCKET = 'post-images';
const KIMI_CHAT_ATTACHMENT_PREFIX = 'kimi-chat';
const CLASSROOM_READ_ACTION_TYPES = new Set<string>([
  'get_classroom_overview',
  'get_class_detail',
  'get_class_students',
  'get_class_lessons',
  'get_lesson_detail',
  'get_class_work',
  'get_work_detail',
  'get_submission_detail',
  'get_class_discussion',
  'get_class_syllabus',
  'get_class_textbooks',
  'get_class_analytics',
  'search_classroom_entities',
]);

export const MAX_CHAT_INPUT_CHARS = 800;

const sanitizeFileName = (name: string): string => {
  const cleaned = name.trim().replace(/[^a-zA-Z0-9._-]/g, '_').slice(0, 80);
  return cleaned || 'upload.bin';
};

const normalizeAttachmentKind = (mimeType: string): KimiChatAttachmentKind =>
  mimeType.toLowerCase().startsWith('image/') ? 'image' : 'file';

const isLikelyHttpUrl = (value: string): boolean => /^https?:\/\//i.test(value.trim());

const sanitizeMessageAttachments = (
  attachments: KimiChatAttachment[] | undefined
): KimiChatAttachment[] | undefined => {
  if (!Array.isArray(attachments) || !attachments.length) return undefined;
  const normalized = attachments
    .slice(0, MAX_ATTACHMENTS_PER_MESSAGE)
    .map((attachment) => {
      if (!attachment || typeof attachment !== 'object') return null;
      const url = typeof attachment.url === 'string' ? attachment.url.trim() : '';
      const name = typeof attachment.name === 'string' ? attachment.name.trim() : '';
      const mimeType = typeof attachment.mimeType === 'string' ? attachment.mimeType.trim() : '';
      const sizeBytes = typeof attachment.sizeBytes === 'number' ? attachment.sizeBytes : 0;
      if (!url || !isLikelyHttpUrl(url) || !name || !mimeType || sizeBytes <= 0) return null;
      return {
        kind: attachment.kind === 'image' || attachment.kind === 'file' ? attachment.kind : normalizeAttachmentKind(mimeType),
        name: name.slice(0, 80),
        mimeType: mimeType.slice(0, 120),
        sizeBytes: Math.trunc(sizeBytes),
        url,
        storagePath:
          typeof attachment.storagePath === 'string' && attachment.storagePath.trim()
            ? attachment.storagePath.trim()
            : undefined,
      } as KimiChatAttachment;
    })
    .filter((entry): entry is KimiChatAttachment => Boolean(entry));
  return normalized.length ? normalized : undefined;
};

export const extractUploadRequestsFromActionResults = (
  actionResults: SermonAgentActionResult[]
): SermonAgentUploadRequest[] =>
  actionResults
    .map((result) => result.uploadRequest)
    .filter((entry): entry is SermonAgentUploadRequest => Boolean(entry && typeof entry.actionId === 'string'));

export const extractClassroomReadResultsFromActionResults = (
  actionResults: SermonAgentActionResult[]
): SermonAgentActionResult[] =>
  actionResults.filter(
    (result) =>
      result.status === 'success' &&
      CLASSROOM_READ_ACTION_TYPES.has(result.type) &&
      !!result.details
  );

export const sanitizeLocalChatHistory = (messages: KimiChatMessage[]): KimiChatMessage[] =>
  messages
    .slice(-MAX_LOCAL_MESSAGES)
    .map((message) => {
      const attachments = sanitizeMessageAttachments(message.attachments);
      return {
        role: message.role,
        content: message.content.trim(),
        ...(attachments ? { attachments } : {}),
      };
    })
    .filter((message) => Boolean(message.content) || Boolean(message.attachments?.length));

export const uploadAttachmentForKimiChat = async (file: File): Promise<KimiChatAttachment> => {
  if (!file || !(file instanceof File)) {
    throw new Error('Select a valid file before sending.');
  }
  if (file.size <= 0) {
    throw new Error('Selected file is empty.');
  }
  if (file.size > MAX_CHAT_ATTACHMENT_BYTES) {
    throw new Error(
      `File is too large. Max allowed size is ${Math.floor(MAX_CHAT_ATTACHMENT_BYTES / 1024 / 1024)}MB.`
    );
  }

  const name = sanitizeFileName(file.name || 'upload.bin');
  const mimeType = (file.type || 'application/octet-stream').trim().toLowerCase();
  const dayKey = new Date().toISOString().slice(0, 10);
  const storagePath = `${KIMI_CHAT_ATTACHMENT_PREFIX}/${dayKey}/${crypto.randomUUID()}-${name}`;

  const { error: uploadError } = await supabase.storage
    .from(KIMI_CHAT_ATTACHMENT_BUCKET)
    .upload(storagePath, file, {
      upsert: false,
      contentType: file.type || undefined,
      cacheControl: '3600',
    });

  if (uploadError) {
    throw new Error(uploadError.message || 'Unable to upload attachment.');
  }

  const { data } = supabase.storage.from(KIMI_CHAT_ATTACHMENT_BUCKET).getPublicUrl(storagePath);
  const publicUrl = data?.publicUrl?.trim();
  if (!publicUrl) {
    throw new Error('Unable to create attachment URL.');
  }

  return {
    kind: normalizeAttachmentKind(mimeType),
    name,
    mimeType,
    sizeBytes: file.size,
    url: publicUrl,
    storagePath,
  };
};

export const requestKimiReply = async (
  messages: KimiChatMessage[],
  options?: {
    confirmActionBundle?: string;
  }
): Promise<{
  assistantMessage: string;
  usage?: KimiChatProxyResponse['usage'];
  context?: KimiChatContextMetadata | null;
  actionResults: SermonAgentActionResult[];
  pendingActionBundle: PendingActionBundle | null;
}> => {
  const sanitized = sanitizeLocalChatHistory(messages);
  const confirmActionBundle = options?.confirmActionBundle?.trim();

  const { data, error } = await supabase.functions.invoke<KimiChatProxyResponse>('kimi-chat-proxy', {
    body: {
      messages: sanitized,
      ...(confirmActionBundle ? { confirmActionBundle } : {}),
    },
  });

  if (error) {
    const details = await extractInvokeErrorDetails(error, {
      functionName: 'kimi-chat-proxy',
      actionName: 'requestKimiReply',
    });
    console.error('[KimiChat] Edge function invoke failed', details, error);
    throw new Error(details.message);
  }

  const assistantMessage = data?.assistantMessage?.trim();
  if (!assistantMessage) {
    console.error('[KimiChat] Empty assistant response', {
      actionName: 'requestKimiReply',
      functionName: 'kimi-chat-proxy',
      edgeFunction: 'kimi-chat-proxy',
      recoverable: false,
      httpStatus: null,
      responseBody: data ?? null,
    });
    throw new Error(data?.error || 'Chat service returned an empty response.');
  }

  return {
    assistantMessage,
    usage: data?.usage,
    context: data?.context ?? null,
    actionResults: (data?.actionResults ?? []).filter(
      (entry: unknown): entry is SermonAgentActionResult =>
        Boolean(entry && typeof entry === 'object' && typeof (entry as { actionId?: unknown }).actionId === 'string')
    ),
    pendingActionBundle:
      data?.pendingActionBundle && typeof data.pendingActionBundle.token === 'string'
        ? data.pendingActionBundle
        : null,
  };
};
